import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import ItemsEditor from '../components/ItemsEditor';
import toast from 'react-hot-toast';

const today  = () => new Date().toISOString().slice(0,10);
const inDays = (d) => new Date(Date.now()+d*86400000).toISOString().slice(0,10);

export default function InvoiceForm() {
  const { id } = useParams(); const nav = useNavigate(); const isEdit = !!id;
  const [clients, setClients] = useState([]);
  const [items, setItems]     = useState([{ item_name:'', description:'', quantity:1, unit_price:0, tax_percent:18 }]);
  const [form, setForm]       = useState({ client_id:'', issue_date:today(), due_date:inDays(30), status:'unpaid', discount:0, notes:'', terms:'Payment due within 30 days.' });
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data.data||[]));
    if (isEdit) {
      api.get(`/invoices/${id}`).then(r => {
        const inv = r.data.data;
        setForm({ client_id:inv.client_id, issue_date:inv.issue_date?.slice(0,10)||today(), due_date:inv.due_date?.slice(0,10)||inDays(30), status:inv.status, discount:inv.discount||0, notes:inv.notes||'', terms:inv.terms||'' });
        setItems(inv.items.map(i=>({ item_name:i.item_name, description:i.description||'', quantity:+i.quantity, unit_price:+i.unit_price, tax_percent:+i.tax_percent })));
      });
    }
  }, [id]);

  const sub   = items.reduce((s,i)=>s+i.quantity*i.unit_price, 0);
  const tax   = items.reduce((s,i)=>s+i.quantity*i.unit_price*(i.tax_percent/100), 0);
  const total = sub + tax - (parseFloat(form.discount)||0);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Select a client'); return; }
    if (!items.length || items.some(i=>!i.item_name)) { toast.error('Fill all item names'); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/invoices/${id}`, { ...form, items }); toast.success('Invoice updated!'); }
      else        { await api.post('/invoices', { ...form, items });       toast.success('Invoice created!'); }
      nav('/invoices');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h2>
          <p className="page-subtitle">{isEdit ? 'Update invoice details' : 'Create a new invoice'}</p></div>
        <button className="btn btn-secondary" onClick={()=>nav('/invoices')}>← Back</button>
      </div>

      <form onSubmit={save}>
        <div className="grid-2" style={{ marginBottom:18 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Invoice Details</span></div>
            <div className="form-group">
              <label className="form-label">Client *</label>
              <select className="form-control" value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value})} required>
                <option value="">— Select Client —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Issue Date</label>
                <input type="date" className="form-control" value={form.issue_date} onChange={e=>setForm({...form,issue_date:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Due Date</label>
                <input type="date" className="form-control" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  <option value="unpaid">Unpaid</option><option value="paid">Paid</option>
                  <option value="draft">Draft</option><option value="sent">Sent</option>
                </select></div>
              <div className="form-group"><label className="form-label">Discount (₹)</label>
                <input type="number" min="0" step=".01" className="form-control" value={form.discount} onChange={e=>setForm({...form,discount:e.target.value})} /></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Notes & Summary</span></div>
            <div className="form-group"><label className="form-label">Notes</label>
              <textarea className="form-control" rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Notes for the client…" /></div>
            <div className="form-group"><label className="form-label">Terms</label>
              <textarea className="form-control" rows={2} value={form.terms} onChange={e=>setForm({...form,terms:e.target.value})} /></div>
            <div style={{ background:'var(--bg4)',borderRadius:'var(--r)',padding:14,marginTop:8 }}>
              {[['Subtotal',`₹${sub.toFixed(2)}`],['Tax',`₹${tax.toFixed(2)}`],['Discount',`- ₹${parseFloat(form.discount||0).toFixed(2)}`]].map(([l,v])=>(
                <div key={l} style={{ display:'flex',justifyContent:'space-between',fontSize:12.5,marginBottom:5,color:'var(--text2)' }}><span>{l}</span><span>{v}</span></div>
              ))}
              <div style={{ display:'flex',justifyContent:'space-between',fontSize:16,fontWeight:800,paddingTop:9,borderTop:'1px solid var(--border)',color:'var(--ok)' }}>
                <span>Total</span><span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom:18 }}>
          <ItemsEditor items={items} setItems={setItems} />
        </div>

        <div style={{ display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button type="button" className="btn btn-secondary" onClick={()=>nav('/invoices')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner"/> : (isEdit ? '✓ Update Invoice' : '✓ Create Invoice')}
          </button>
        </div>
      </form>
    </div>
  );
}
